/**
 * One API error type + one user-message function for every request layer
 * (useApiRequest, integrationsApi, supportApi, the dashboard / firm hooks).
 *
 * Rule: a user never sees a raw `Error.message` from a failed fetch or an MSAL token call
 * ("Failed to fetch", "interaction_required"). Server-authored text is shown for 4xx and for
 * 5xx that carry a deliberate error code; a bare 500 (generic / INTERNAL_ERROR) gets our own
 * sentence plus the server's `httpRequestId` so support can correlate the failure.
 */

export class ApiError extends Error {
    /**
     * @param {string} message  user-safe sentence (already status-appropriate)
     * @param {number} [status] HTTP status
     * @param {string} [code]   backend ErrorCode when the body carried one
     * @param {object} [details] `error.details` from the body (may hold httpRequestId)
     */
    constructor(message, status, code, details) {
        super(message);
        this.name = 'ApiError';
        this.status = status;
        this.code = code;
        this.details = details ?? null;
        this.httpRequestId = (details && details.httpRequestId) || null;
    }
}

const withRef = (text, id) => (id ? `${text} Reference: ${id}` : text);

/** Plain sentence for an HTTP status when the server supplied none usable. */
export function messageForStatus(status, httpRequestId) {
    if (status === 401) return 'Your session has expired. Please sign in again.';
    if (status === 403) return 'You are not permitted to do this.';
    if (status === 404) return 'We could not find what you asked for.';
    if (status === 413) return 'The file is too large. The maximum upload size is 20 MB.';
    if (status === 429) return 'Too many requests. Please wait a moment and try again.';
    if (status >= 500) return withRef('Something went wrong on our side. Please try again.', httpRequestId);
    if (status < 400) return 'The server sent an unexpected response. Please try again.';
    return `The request could not be completed (error ${status}).`;
}

const CONFIGURATION_MESSAGE = 'This feature is not set up on this environment yet. Please ask your platform administrator to configure it.';
const NON_DELIBERATE_5XX_CODES = new Set(['INTERNAL_ERROR', 'CONFIGURATION_ERROR', 'SERVICE_UNAVAILABLE']);

/**
 * Builds an ApiError from a fetch Response + its parsed JSON envelope (or null).
 * 4xx keeps the server's own message (the contract authors it for users); 5xx never does.
 */
export const PLATFORM_ADMIN_APPROVAL_DENIED = 'PLATFORM_ADMIN_APPROVAL_DENIED';
const ADMIN_APPROVAL_FALLBACK =
    'Platform admins cannot approve, publish or finalize this. The Lead Advisor or the Company SPOC must do it.';
// The API's refusal text names the roles that may act: "Platform admin cannot <action>; <roles> must."
const ADMIN_APPROVAL_TEXT = /^Platform admin cannot [^;]+; .+ must\.$/;

// Credential routes answer 503 with this code when the server has no KEK. The server's sentence is
// deliberate, user-authored text ("ask your platform administrator"): shown verbatim plus the reference,
// never replaced by the generic 5xx sentence, and never retried automatically (a retry cannot succeed).
export const CREDENTIAL_STORE_NOT_CONFIGURED = 'CREDENTIAL_STORE_NOT_CONFIGURED';
const CREDENTIAL_STORE_FALLBACK =
    'Credential storage is not configured on the server. Ask your platform administrator to set CREDENTIAL_KEK_SECRET.';

/** True for an ApiError raised by the credential-store-not-configured refusal. */
export const isCredentialStoreUnconfigured = (err) => !!err && err.code === CREDENTIAL_STORE_NOT_CONFIGURED;

// Verify route answers 501 with this code for providers that have no live verifier. It is a deliberate,
// user-authored sentence (not a fault): shown verbatim, no generic "not set up" text and no reference suffix.
export const VERIFY_NOT_SUPPORTED = 'VERIFY_NOT_SUPPORTED';
const VERIFY_NOT_SUPPORTED_FALLBACK =
    "Live verification is not supported for this provider. The saved key can't be checked from here; it will be used when you run a lookup.";

/** True for an ApiError raised by the verify-not-supported (501) answer. */
export const isVerifyNotSupported = (err) => !!err && err.code === VERIFY_NOT_SUPPORTED;

export function apiErrorFromResponse(res, json) {
    const status = res.status;
    const err = json?.error;
    const requestId = err?.details?.httpRequestId || null;
    const serverMessage = typeof err?.message === 'string' && err.message.trim() ? err.message : null;
    // A bare 500 (no code, or INTERNAL_ERROR) carries only a generic/raw message. A 5xx with a
    // deliberate code (e.g. UPSTREAM_ERROR from a provider call) carries user-authored text.
    // CONFIGURATION_ERROR / SERVICE_UNAVAILABLE carry env-var names and raw provider errors
    // ("GRAPH_CLIENT_SECRET is not configured"), so they are never shown.
    const deliberate = status < 500 || (err?.code && !NON_DELIBERATE_5XX_CODES.has(err.code));
    let message = !deliberate || !serverMessage
        ? messageForStatus(status, requestId)
        : serverMessage;
    // A missing server-side key/setting (e.g. the AI provider key behind technical evaluation) is not the
    // user's fault and "try again" will not help: say so plainly, still without naming the setting.
    if (status >= 500 && err?.code === 'CONFIGURATION_ERROR') message = CONFIGURATION_MESSAGE;
    if (err?.code === CREDENTIAL_STORE_NOT_CONFIGURED) message = serverMessage || CREDENTIAL_STORE_FALLBACK;
    if (err?.code === VERIFY_NOT_SUPPORTED) return new ApiError(serverMessage || VERIFY_NOT_SUPPORTED_FALLBACK, status, err.code, err.details);
    // Callers that print err.message directly must still show the reference for any 5xx.
    if (status >= 500 && requestId && !message.includes(requestId)) message = withRef(message, requestId);
    // PRD s2.2: approvals belong to the Lead Advisor / Company SPOC. Show the server's sentence when it names
    // the roles; otherwise a plain sentence of our own so a bare code never reaches the user.
    if (status === 403 && (err?.code === PLATFORM_ADMIN_APPROVAL_DENIED || ADMIN_APPROVAL_TEXT.test(serverMessage || ''))) {
        message = ADMIN_APPROVAL_TEXT.test(serverMessage || '') ? serverMessage : ADMIN_APPROVAL_FALLBACK;
        return new ApiError(message, status, PLATFORM_ADMIN_APPROVAL_DENIED, err?.details);
    }
    return new ApiError(message, status, err?.code, err?.details);
}

const CONNECTION_MESSAGE = "Couldn't reach the server - check your connection and try again.";
const SESSION_MESSAGE = "Your sign-in could not be refreshed. Please sign in again.";

function isNetworkFailure(err) {
    if (!err) return false;
    const text = `${err.name || ''} ${err.message || ''}`;
    return err instanceof TypeError || /failed to fetch|networkerror|load failed|network request failed|aborterror/i.test(text);
}

function isAuthFailure(err) {
    if (!err) return false;
    const text = `${err.name || ''} ${err.errorCode || ''} ${err.message || ''}`;
    return /AuthError|InteractionRequired|interaction_required|login_required|consent_required|no_account|token_renewal|monitor_window|BrowserAuth|ClientAuth/i.test(text);
}

/**
 * The one function every catch block uses to turn a thrown value into user text.
 * @param {unknown} err
 * @param {string} [fallback] shown when the error is unrecognised (never the raw message)
 */
export function userMessageForError(err, fallback = 'Something went wrong. Please try again.') {
    if (err instanceof ApiError) {
        // ApiError messages are built user-safe; make sure a 5xx always carries its reference.
        if (err.status >= 500 && err.code !== VERIFY_NOT_SUPPORTED && err.httpRequestId &&!err.message.includes(err.httpRequestId)) {
            return withRef(err.message, err.httpRequestId);
        }
        return err.message || messageForStatus(err.status);
    }
    if (isAuthFailure(err)) return SESSION_MESSAGE;
    if (isNetworkFailure(err)) return CONNECTION_MESSAGE;
    return fallback;
}

/**
 * Wraps a network / MSAL failure thrown inside a request layer as an ApiError (status 0) whose
 * message is already user-safe, so callers that print `err.message` never show "Failed to fetch".
 * ApiErrors pass through untouched.
 */
export function toApiError(err) {
    if (err instanceof ApiError) return err;
    const wrapped = new ApiError(userMessageForError(err), 0);
    wrapped.cause = err;
    return wrapped;
}

/**
 * Opens a blank tab synchronously inside a click handler so popup blockers allow it, ready to be
 * handed to `openServerUrl(url, win)` once the async fetch of the URL resolves.
 * Returns null when the browser blocked it (openServerUrl then falls back to window.open).
 */
export function preopenWindow() {
    const win = window.open('', '_blank');
    if (win) {
        try { win.opener = null; } catch { /* cross-origin */ }
    }
    return win || null;
}

/**
 * Opens a server-issued URL (SAS download, e-sign link) in a new tab. https-only — a
 * `javascript:`/`data:`/`http:` URL from a compromised or misconfigured response is refused.
 * Pass `win` (a window opened synchronously inside the click handler) to avoid popup blockers
 * when the URL is only known after an await.
 * @returns {boolean} true when the URL was opened; false when refused (not https) or popup-blocked
 */
export function openServerUrl(url, win) {
    let ok = false;
    try {
        ok = typeof url === 'string' && new URL(url).protocol === 'https:';
    } catch {
        ok = false;
    }
    if (!ok) {
        if (win && !win.closed) win.close();
        return false;
    }
    if (win && !win.closed) {
        try { win.opener = null; } catch { /* cross-origin */ }
        win.location.href = url;
        return true;
    }
    // No 'noopener' feature here: it makes window.open return null even on success, which would be
    // indistinguishable from a blocked popup. The opener is severed manually instead.
    const opened = window.open(url, '_blank');
    if (!opened) return false;
    try { opened.opener = null; } catch { /* cross-origin */ }
    return true;
}

/** True when `url` is an https URL openServerUrl would accept (distinguishes "invalid" from "blocked"). */
export function isOpenableUrl(url) {
    try {
        return typeof url === 'string' && new URL(url).protocol === 'https:';
    } catch {
        return false;
    }
}
